import { SagaReturnType, call, put, takeEvery } from 'redux-saga/effects';
import { monthCloseOutAlertConstants } from '../../actions/constant';
import monthlyCloseoutService from 'src/services/apiServices/monthlyCloseOut/monthlyCloseoutService';

/**
 * BOPS-115 — create / edit / cancel a custom closeout period.
 *
 * Every rule that governs these (one calendar month, no overlap, not a full month, month not
 * already closed monthly, never-closed-before-editing) is enforced server side and comes back as a
 * 400 with a human-readable message. The sagas therefore surface `message` verbatim rather than
 * re-deriving the reason — the backend names the conflicting period, which the UI can't know.
 */

const {
  CREATE_CUSTOM_CLOSEOUT_REQUEST,
  CREATE_CUSTOM_CLOSEOUT_SUCCESS,
  CREATE_CUSTOM_CLOSEOUT_FAILURE,
  CREATE_CUSTOM_CLOSEOUT_LOADING,
  UPDATE_CUSTOM_CLOSEOUT_REQUEST,
  UPDATE_CUSTOM_CLOSEOUT_SUCCESS,
  UPDATE_CUSTOM_CLOSEOUT_FAILURE,
  UPDATE_CUSTOM_CLOSEOUT_LOADING,
  CANCEL_CUSTOM_CLOSEOUT_REQUEST,
  CANCEL_CUSTOM_CLOSEOUT_SUCCESS,
  CANCEL_CUSTOM_CLOSEOUT_FAILURE,
  CANCEL_CUSTOM_CLOSEOUT_LOADING,
} = monthCloseOutAlertConstants;

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

function* createCustomCloseout(action: any): SagaReturnType<any> {
  try {
    yield put({ type: CREATE_CUSTOM_CLOSEOUT_LOADING });
    const response = yield call(monthlyCloseoutService.createCustomCloseout, action.payload);

    if (response?.status === 200 || response?.status === 201) {
      yield put({ type: CREATE_CUSTOM_CLOSEOUT_SUCCESS, payload: response.data });
    } else {
      throw response.data;
    }
  } catch (error: any) {
    yield put({
      type: CREATE_CUSTOM_CLOSEOUT_FAILURE,
      payload: { message: errorMessage(error, 'Failed to create closeout period.') },
    });
  }
}

function* updateCustomCloseout(action: any): SagaReturnType<any> {
  try {
    yield put({ type: UPDATE_CUSTOM_CLOSEOUT_LOADING });
    const response = yield call(monthlyCloseoutService.updateCustomCloseoutDates, action.payload);

    if (response?.status === 200) {
      yield put({ type: UPDATE_CUSTOM_CLOSEOUT_SUCCESS, payload: response.data });
    } else {
      throw response.data;
    }
  } catch (error: any) {
    yield put({
      type: UPDATE_CUSTOM_CLOSEOUT_FAILURE,
      payload: { message: errorMessage(error, 'Failed to update closeout period.') },
    });
  }
}

function* cancelCustomCloseout(action: any): SagaReturnType<any> {
  try {
    yield put({ type: CANCEL_CUSTOM_CLOSEOUT_LOADING });
    const response = yield call(monthlyCloseoutService.cancelCustomCloseout, action.payload);

    if (response?.status === 200 || response?.status === 204) {
      yield put({ type: CANCEL_CUSTOM_CLOSEOUT_SUCCESS, payload: response.data });
    } else {
      throw response.data;
    }
  } catch (error: any) {
    yield put({
      type: CANCEL_CUSTOM_CLOSEOUT_FAILURE,
      payload: { message: errorMessage(error, 'Failed to cancel closeout period.') },
    });
  }
}

export function* createCustomCloseoutSaga() {
  yield takeEvery(CREATE_CUSTOM_CLOSEOUT_REQUEST, createCustomCloseout);
}

export function* updateCustomCloseoutSaga() {
  yield takeEvery(UPDATE_CUSTOM_CLOSEOUT_REQUEST, updateCustomCloseout);
}

export function* cancelCustomCloseoutSaga() {
  yield takeEvery(CANCEL_CUSTOM_CLOSEOUT_REQUEST, cancelCustomCloseout);
}
